import React, { useState, useEffect } from "react";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { message } from "antd";
import PremiumFooter from "./footer";
import mayankLogo from "../../assets/images/logo-2.png";
import sparLogo   from "../../assets/logo_car.png";
import brandLogo  from "../../assets/real.svg";
import { setCookie }  from "../../utilities/utll";
import { APIURL }     from "../../utilities/api";
import TopLoader      from "../../utilities/topLoader";
import { ForgotPasswordModal } from "./forgotPassword";

const TYPED_TEXT  = "Built for\nthe way\nyou work.";
const TYPE_SPEED  = 55; // ms per character

const ElegantLogin = () => {
  const [userId,   setUserId]   = useState("");
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [remember, setRemember] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [on,  setOn]  = useState(false);
  const [pct, setPer] = useState(0);
  const [openForgot, setOpenForgot] = useState(false);
  const [uidErr, setUidErr] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [touched, setTouched] = useState({ uid: false, pwd: false });
  const [focus,   setFocus]   = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  // typing effect state
  const [typed, setTyped]           = useState("");
  const [doneTyping, setDoneTyping] = useState(false);

  /* ── remember-me restore ── */
  useEffect(() => {
    const id  = localStorage.getItem("rememberedUserId");
    const pw  = localStorage.getItem("rememberedPassword");
    const rem = localStorage.getItem("rememberMe") === "true";
    if (rem && id) { setUserId(id); setRemember(true); if (pw) setPassword(atob(pw)); }
  }, []);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── typing effect ── */
  useEffect(() => {
    let i = 0;
    // small initial delay before typing starts
    const start = setTimeout(() => {
      const iv = setInterval(() => {
        i++;
        setTyped(TYPED_TEXT.slice(0, i));
        if (i >= TYPED_TEXT.length) {
          clearInterval(iv);
          setDoneTyping(true);
        }
      }, TYPE_SPEED);
      return () => clearInterval(iv);
    }, 400);
    return () => clearTimeout(start);
  }, []);

  const vUid = v => (!v.trim() ? "User ID is required" : v.length < 3 ? "Minimum 3 characters" : "");
  const vPwd = v => (!v ? "Password is required" : v.length < 5 ? "Minimum 5 characters" : "");

  const handleSignIn = async () => {
    try {
      setOn(true); setPer(15);
      setTouched({ uid: true, pwd: true });
      const uE = vUid(userId), pE = vPwd(password);
      setUidErr(uE); setPwdErr(pE); setPer(45);
      if (uE || pE) { await new Promise(r => setTimeout(r, 300)); setPer(0); setOn(false); return; }
      setPer(65);
      const ctrl = new AbortController();
      const tid  = setTimeout(() => ctrl.abort(), 15000);
      const res  = await fetch(`${APIURL}Users/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userName: userId, password }), signal: ctrl.signal,
      });
      clearTimeout(tid); setPer(88);
      let val; try { val = await res.json(); } catch { val = null; }
      setPer(100);
      if (res.ok && val?.IsSuccess && val?.StatusCode === 200) {
        await new Promise(r => setTimeout(r, 200));
        messageApi.success("Login successful");
        setCookie("auth", true);
        setCookie("userDetail", JSON.stringify(val.Data?.User) ?? "");
        setCookie("token", val.Data?.AccessToken ?? "");
        if (remember) {
          localStorage.setItem("rememberedUserId", userId);
          localStorage.setItem("rememberedPassword", btoa(password));
          localStorage.setItem("rememberMe", "true");
        } else {
          ["rememberedUserId","rememberedPassword","rememberMe"].forEach(k => localStorage.removeItem(k));
        }
        window.location.href = "/postlogin";
      } else {
        await new Promise(r => setTimeout(r, 200));
        messageApi.error("Incorrect credentials. Please try again.");
      }
    } catch { messageApi.error("Network error. Check your connection."); }
    finally { setTimeout(() => { setPer(0); setOn(false); }, 300); }
  };

  /* ── render typed headline with line breaks ── */
  const renderTyped = () =>
    typed.split("\n").map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

  return (
    <>
      {contextHolder}
      <TopLoader isOn={on} percentage={pct} height={2} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── SHELL ─────────────────────────────────────────────────── */
        .ap-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', -apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }

        /* ── LEFT PANEL ───────────────────────────────────────────── */
        .ap-left {
          width: 46%;
          min-height: 100vh;
          background: #0a0a0a;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 52px 56px;
          position: relative;
          flex-shrink: 0;
          overflow: hidden;
        }

        /* very subtle inner glow — keeps flat black from feeling dead */
        .ap-left::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 70% 60% at 30% 50%, rgba(255,255,255,0.018) 0%, transparent 70%);
          pointer-events: none;
        }

        /* brand logo — absolute top-left */
        .ap-left-logo {
          position: absolute;
          top: 48px;
          left: 56px;
          height: 23px;
          width: auto;
          object-fit: contain;
          filter: brightness(0) invert(1);
          opacity: 0.88;
          z-index: 2;
        }

        /* centered copy block */
        .ap-left-body {
          position: relative;
          z-index: 2;
          max-width: 340px;
        }

        .ap-left-tag {
          display: block;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.3);
          margin-bottom: 22px;
        }

        /* typed headline */
        .ap-left-h1 {
          font-size: 50px;
          font-weight: 700;
          line-height: 1.07;
          letter-spacing: -2.6px;
          color: #ffffff;
          min-height: 170px; /* prevents layout jump while typing */
        }

        /* the faded last word */
        .ap-left-h1 .faded { color: rgba(255,255,255,0.32); }

        /* blinking cursor */
        .ap-cursor {
          display: inline-block;
          width: 2px;
          height: 0.85em;
          background: rgba(255,255,255,0.55);
          margin-left: 3px;
          vertical-align: middle;
          border-radius: 1px;
          animation: apCursorBlink 1s step-start infinite;
        }
        /* solid (no blink) while actively typing */
        .ap-cursor.typing { animation: none; opacity: 1; }

        @keyframes apCursorBlink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }

        /* description — fades in after typing completes */
        .ap-left-desc {
          margin-top: 22px;
          font-size: 13.5px;
          font-weight: 400;
          line-height: 1.75;
          color: rgba(255,255,255,0.38);
          max-width: 288px;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.55s ease 0.1s, transform 0.55s ease 0.1s;
        }
        .ap-left-desc.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── RIGHT PANEL ──────────────────────────────────────────── */
        .ap-right {
          flex: 1;
          min-height: 100vh;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          border-left: 1px solid #ebebeb;
        }

        /* spar logo top-right (desktop) */
        .ap-right-top {
          display: flex;
          justify-content: flex-end;
          padding: 44px 56px 0;
        }
        .ap-spar { height: 58px; width: auto; object-fit: contain; }

        /* mobile top bar */
        .ap-mobile-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 36px 28px 0;
        }
        .ap-mobile-brand { height: 20px; object-fit: contain; }
        .ap-mobile-spar  { height: 30px; object-fit: contain; }

        /* form centering wrapper */
        .ap-form-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 60px;
        }

        /* form content — fixed width so everything aligns to the same axis */
        .ap-form {
          width: 100%;
          max-width: 352px;
        }

        /* ── form heading ── */
        .ap-form-title {
          font-size: 30px;
          font-weight: 800;
          letter-spacing: -1.2px;
          color: #1d1d1f;
          margin-bottom: 6px;
          line-height: 1.1;
        }
        .ap-form-sub {
          font-size: 13.5px;
          color: #86868b;
          margin-bottom: 32px;
          line-height: 1.55;
        }

        /* ── field ── */
        .ap-field { margin-bottom: 14px; }

        .ap-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #6e6e73;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 7px;
        }

        .ap-input-wrap { position: relative; }

        .ap-input {
          width: 100%;
          height: 50px;
          padding: 0 46px 0 15px;
          border: 1px solid #d2d2d7;
          border-radius: 10px;
          font-size: 15px;
          font-family: inherit;
          font-weight: 400;
          color: #1d1d1f;
          background: #ffffff;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          -webkit-appearance: none;
        }
        .ap-input::placeholder { color: #c7c7cc; }
        .ap-input:focus {
          border-color: #1d1d1f;
          box-shadow: 0 0 0 3px rgba(29,29,31,0.09);
        }
        .ap-input.is-err { border-color: #ff3b30; background: #fff8f8; }
        .ap-input.is-err:focus { box-shadow: 0 0 0 3px rgba(255,59,48,0.12); }

        .ap-eye {
          position: absolute;
          right: 13px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none;
          padding: 4px; cursor: pointer;
          color: #aeaeb2;
          display: flex; align-items: center;
          transition: color 0.12s;
        }
        .ap-eye:hover { color: #48484a; }

        .ap-err-msg {
          margin-top: 5px;
          font-size: 11.5px;
          color: #ff3b30;
          display: flex; align-items: center; gap: 4px;
          animation: apErrIn 0.15s ease;
        }
        @keyframes apErrIn {
          from { opacity:0; transform: translateY(-3px); }
          to   { opacity:1; transform: translateY(0); }
        }

        /* ── options row ── */
        .ap-opts {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 16px 0 24px;
        }

        .ap-chk-wrap {
          display: flex; align-items: center; gap: 8px;
          cursor: pointer; user-select: none;
        }
        .ap-chk {
          appearance: none; -webkit-appearance: none;
          width: 16px; height: 16px;
          border: 1px solid #c7c7cc; border-radius: 4px;
          background: #fff; cursor: pointer; position: relative;
          flex-shrink: 0; transition: all 0.15s;
        }
        .ap-chk:checked { background: #1d1d1f; border-color: #1d1d1f; }
        .ap-chk:checked::after {
          content: '';
          position: absolute;
          left: 4px; top: 1.5px;
          width: 5px; height: 9px;
          border: 1.5px solid #fff;
          border-top: none; border-left: none;
          transform: rotate(45deg);
        }
        .ap-chk-lbl { font-size: 13px; color: #48484a; }

        .ap-forgot {
          background: none; border: none;
          font-family: inherit; font-size: 13px; font-weight: 500;
          color: #1d1d1f; cursor: pointer; padding: 0;
          text-decoration: underline;
          text-underline-offset: 2px;
          text-decoration-color: rgba(29,29,31,0.3);
          transition: text-decoration-color 0.15s;
        }
        .ap-forgot:hover { text-decoration-color: #1d1d1f; }

        /* ── CTA button ── */
        .ap-cta {
          width: 100%; height: 50px;
          background: #1d1d1f; color: #ffffff;
          border: none; border-radius: 10px;
          font-family: inherit; font-size: 15px; font-weight: 600;
          letter-spacing: -0.2px; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 9px;
          transition: background 0.15s, transform 0.12s;
          -webkit-appearance: none;
        }
        .ap-cta:hover { background: #2d2d2f; transform: translateY(-1px); }
        .ap-cta:active { background: #111113; transform: translateY(0); }
        .ap-cta-arr { transition: transform 0.15s; }
        .ap-cta:hover .ap-cta-arr { transform: translateX(3px); }

        /* ── below-form footer (Mayank + disclaimer) — inside form, stays at form width ── */
        .ap-form-footer {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .ap-mayank-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .ap-mayank-label {
          font-size: 13px;
          font-weight: 500;
          color: #48484a;
          letter-spacing: 0.01em;
        }
        .ap-mayank-logo {
          height: 28px;
          width: auto;
          object-fit: contain;
          opacity: 0.88;
        }

        /* ── responsive ── */
        @media (max-width: 900px) {
          .ap-left        { display: none; }
          .ap-right       { border-left: none; }
          .ap-right-top   { display: none; }
          .ap-form-wrap   { padding: 40px 28px; align-items: flex-start; }
          .ap-form        { max-width: 100%; }
          .ap-form-title  { font-size: 26px; }
        }
        @media (max-width: 480px) {
          .ap-form-wrap   { padding: 32px 20px; }
          .ap-form-title  { font-size: 22px; }
          .ap-mobile-head { padding: 28px 20px 0; }
        }
      `}</style>

      <div className="ap-root">

        {/* ── LEFT: Brand / typing panel ── */}
        {!isMobile && (
          <div className="ap-left">
            <img src={brandLogo} alt="Hypermax City" className="ap-left-logo" />

            <div className="ap-left-body">
              <span className="ap-left-tag">Vendor Portal</span>

              <h2 className="ap-left-h1">
                {renderTyped()}
                <span className={`ap-cursor${doneTyping ? "" : " typing"}`} />
              </h2>

              <p className={`ap-left-desc${doneTyping ? " visible" : ""}`}>
                The complete platform for procurement, vendor compliance,
                and inventory — built for Carrefour's scale.
              </p>
            </div>
          </div>
        )}

        {/* ── RIGHT: Form panel ── */}
        <div className="ap-right">

          {isMobile ? (
            <div className="ap-mobile-head">
              <img src={brandLogo} alt="Hypermax City" className="ap-mobile-brand" />
              <img src={sparLogo}  alt="Carrefour"     className="ap-mobile-spar"  />
            </div>
          ) : (
            <div className="ap-right-top">
              <img src={sparLogo} alt="Carrefour" className="ap-spar" />
            </div>
          )}

          <div className="ap-form-wrap">
            <div className="ap-form">

              <h1 className="ap-form-title">Sign in.</h1>
              <p className="ap-form-sub">Enter your credentials to continue.</p>

              {/* User ID */}
              <div className="ap-field">
                <label className="ap-label">User ID</label>
                <div className="ap-input-wrap">
                  <input
                    type="text"
                    className={`ap-input${uidErr && touched.uid ? " is-err" : ""}`}
                    placeholder="Enter your user ID"
                    value={userId}
                    onChange={e => { setUserId(e.target.value); if (touched.uid) setUidErr(vUid(e.target.value)); }}
                    onFocus={() => setFocus("uid")}
                    onBlur={() => { setFocus(null); setTouched(p => ({ ...p, uid: true })); setUidErr(vUid(userId)); }}
                    onKeyDown={e => e.key === "Enter" && handleSignIn()}
                  />
                </div>
                {uidErr && touched.uid && (
                  <div className="ap-err-msg">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {uidErr}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="ap-field">
                <label className="ap-label">Password</label>
                <div className="ap-input-wrap">
                  <input
                    type={showPwd ? "text" : "password"}
                    className={`ap-input${pwdErr && touched.pwd ? " is-err" : ""}`}
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); if (touched.pwd) setPwdErr(vPwd(e.target.value)); }}
                    onFocus={() => setFocus("pwd")}
                    onBlur={() => { setFocus(null); setTouched(p => ({ ...p, pwd: true })); setPwdErr(vPwd(password)); }}
                    onKeyDown={e => e.key === "Enter" && handleSignIn()}
                  />
                  <button type="button" className="ap-eye" onClick={() => setShowPwd(p => !p)}>
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pwdErr && touched.pwd && (
                  <div className="ap-err-msg">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {pwdErr}
                  </div>
                )}
              </div>

              {/* Remember + Forgot */}
              <div className="ap-opts">
                <label className="ap-chk-wrap">
                  <input type="checkbox" className="ap-chk" checked={remember} onChange={e => setRemember(e.target.checked)} />
                  <span className="ap-chk-lbl">Remember me</span>
                </label>
                <button className="ap-forgot" onClick={() => setOpenForgot(true)}>
                  Forgot password?
                </button>
              </div>

              {/* Sign in */}
              <button className="ap-cta" onClick={handleSignIn}>
                <span>Continue</span>
                <ArrowRight className="ap-cta-arr" size={16} />
              </button>

              {/* ── Below-form footer: Mayank logo + 2026 disclaimer ── */}
              {/* Both sit inside max-width:352px so they're perfectly centered with the form */}
              <div className="ap-form-footer">
                <div className="ap-mayank-wrap">
                  <span className="ap-mayank-label">Developed by</span>
                  <img src={mayankLogo} alt="Mayank Softwares Solution" className="ap-mayank-logo" />
                </div>
                <PremiumFooter />
              </div>

            </div>
          </div>

        </div>
      </div>

      <ForgotPasswordModal open={openForgot} onCancel={() => setOpenForgot(false)} />
    </>
  );
};

export default ElegantLogin;
